import { create } from "zustand";
import { persist } from "zustand/middleware";

type Theme = "light" | "dark" | "system";

interface UiState {
  sidebarCollapsed: boolean;
  mobileMenuOpen: boolean;
  alertPanelOpen: boolean;
  theme: Theme;

  // Actions
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileMenuOpen: (open: boolean) => void;
  toggleMobileMenu: () => void;
  setAlertPanelOpen: (open: boolean) => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

// Apply theme to document
const applyTheme = (theme: Theme) => {
  if (typeof window === "undefined") return;

  const root = document.documentElement;
  const isDark =
    theme === "dark" ||
    (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);

  if (isDark) {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
};

// Listen for system preference changes
let systemPreferenceListener: (() => void) | null = null;

const setupSystemPreferenceListener = (theme: Theme) => {
  if (typeof window === "undefined") return;

  // Remove existing listener if any
  if (systemPreferenceListener) {
    systemPreferenceListener();
    systemPreferenceListener = null;
  }

  // Only listen when theme is 'system'
  if (theme === "system") {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme("system");
    mediaQuery.addEventListener("change", handler);
    systemPreferenceListener = () => mediaQuery.removeEventListener("change", handler);
  }
};

export const useUiStore = create<UiState>()(
  persist(
    (set, get) => ({
      sidebarCollapsed: false,
      mobileMenuOpen: false,
      alertPanelOpen: false,
      theme: "system",

      toggleSidebar: () => {
        set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setSidebarCollapsed: (collapsed: boolean) => {
        set({ sidebarCollapsed: collapsed });
      },

      setMobileMenuOpen: (open: boolean) => {
        set({ mobileMenuOpen: open });
      },

      toggleMobileMenu: () => {
        set((state) => ({ mobileMenuOpen: !state.mobileMenuOpen }));
      },

      setAlertPanelOpen: (open: boolean) => {
        set({ alertPanelOpen: open });
      },

      setTheme: (theme: Theme) => {
        set({ theme });
        applyTheme(theme);
        setupSystemPreferenceListener(theme);
      },

      toggleTheme: () => {
        const currentTheme = get().theme;
        const newTheme: Theme =
          currentTheme === "light" ? "dark" : currentTheme === "dark" ? "system" : "light";
        set({ theme: newTheme });
        applyTheme(newTheme);
        setupSystemPreferenceListener(newTheme);
      },
    }),
    {
      name: "ui-storage",
      partialize: (state) => ({
        sidebarCollapsed: state.sidebarCollapsed,
        theme: state.theme,
        // mobileMenuOpen not persisted - always start closed
      }),
      onRehydrateStorage: () => (state) => {
        // Apply theme and set up listener on rehydration (page load)
        if (state) {
          applyTheme(state.theme);
          setupSystemPreferenceListener(state.theme);
        }
      },
    }
  )
);

// Initial theme application for first-time users (before rehydration completes)
// This prevents flash of wrong theme on initial load
if (typeof window !== "undefined") {
  applyTheme("system");
}
